import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { captureException } from '@sentry/react';
import {
  type BindOnboardingPhoneInput,
  type OnboardingDraftFormDataInput,
  type OnboardingProduct,
  type OnboardingSessionChallenge,
  type OnboardingSessionVerifyResult,
  paidolService,
  type RegisterOnboardInput,
  type SaveOnboardingDraftInput,
  type StartOnboardingSessionInput,
  type VerifyOnboardingCodeInput,
} from 'app/services/paidol.service';
import { CODE_SCREEN_STATUSES } from 'app/pages/onboard/onboardSessionMessages';
import serializeError, { ErrorLike } from 'util/serializeError';
import { HighnoteValidationError, type HighnoteFieldError } from 'app/services/highnoteValidationError';
import translateOrKey from 'util/translateOrKey';

// Task 2.1: derived from the CDK input type, not Amplify's `UpdateOnboardInput`, which disappears
// from API.ts when the mutation is nulled in group 4. Still the *input* shape rather than the output
// one — input types omit `__typename` and match what each step form produces via getValues().
export type OnboardFormDataDraft = Partial<OnboardingDraftFormDataInput>;

/**
 * What the pre-auth flow knows about the application. Replaces the Amplify `Onboard` model, which
 * carried a row `id` the client no longer sees or needs: the session grant names the draft
 * server-side, so there is nothing here for a caller to guess at.
 */
export interface OnboardDraft {
  invitationCode?: string | null;
  formData?: OnboardFormDataDraft | null;
  /**
   * Tier rebate rate, e.g. `1.5`. Drives the issuing-permission consent label.
   *
   * Nullable and meaningfully `0`, so every read must use `!= null` rather than truthiness — a falsy
   * check shows the "no fees" disclosure to a 0% tier, which is the one tier where the wrong label is
   * plausible enough that nobody would report it.
   */
  rebatePercentage?: number | null;
}

/**
 * Phone-verification gate state (SEE-1274). Deliberately **not** persisted: Req 1.6 requires a
 * fresh verification on every visit, so this lives in memory and dies with the tab.
 *
 * `status` mirrors `OnboardingSessionChallenge.status` from the server rather than being re-derived
 * here, so a new refusal reason surfaces without a frontend change (Req 2.3).
 */
export interface OnboardSessionState {
  /** Carried from screen one; `bindOnboardingPhone` and `verifyOnboardingCode` both need it. */
  invitationCode: string | null;
  status: string | null;
  maskedPhone: string | null;
  /**
   * The number the applicant typed on `PhoneEntry`, kept only so Resend can re-send to it.
   *
   * Resend on a pre-verify draft is `bindOnboardingPhone` called again with the same number, and
   * the server will not echo an unverified number back — so if the client does not hold it, the
   * only way to resend is to ask for it a second time, which Travis has ruled out. Held in memory
   * for the life of the tab like the rest of this slice, never persisted, never logged, and
   * scrubbed the moment a successful verify makes it redundant.
   *
   * Null for a returning applicant whose number is already verified server-side: that draft resends
   * through `startOnboardingSession`, which needs no number.
   */
  pendingPhone: string | null;
  /** Epoch ms after which another code may be requested. Null when no code is outstanding. */
  resendAvailableAt: number | null;
  /** Minted by `verifyOnboardingCode`. Every draft read/save and the final submit carries it. */
  grant: string | null;
  attemptsRemaining: number | null;
  pending: boolean;
  error: string | null;
}

export interface OnboardState {
  onboard: OnboardDraft | null | undefined;
  formDataDraft: OnboardFormDataDraft | null;
  highnoteProducts?: OnboardingProduct[];
  session: OnboardSessionState;
}

const initialSessionState: OnboardSessionState = {
  invitationCode: null,
  status: null,
  maskedPhone: null,
  pendingPhone: null,
  resendAvailableAt: null,
  grant: null,
  attemptsRemaining: null,
  pending: false,
  error: null,
};

const initialState: OnboardState = {
  onboard: null,
  formDataDraft: null,
  highnoteProducts: undefined,
  session: initialSessionState,
};

/**
 * `registerOnboardFunction` tags an error it has already reported so the catch below does not
 * double-report it. Narrowing beats the `error: any` the older thunks use — CLAUDE.md bans `any`.
 */
const wasAlreadyCaptured = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && '__sentryCaptured' in error;

const toError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

/**
 * Marks a rejection as "the session died", not "the save was invalid".
 *
 * Carried on the rejection's `name` because that is the one field `ErrorLike` already has that no
 * server message competes for, so the reducer can act on it without parsing copy. The reducer clears
 * the grant on seeing it, which is what turns a dead end into a route back to re-verification.
 */
export const ONBOARD_SESSION_EXPIRED = 'OnboardSessionExpired';

/**
 * Task 2.2. Reads the draft by session grant, not by invitation code — the grant is what names a
 * draft server-side. Returns a single object rather than the `items[0]` of a list connection, and
 * `found: false` where the Amplify query returned an empty list.
 *
 * SSN and tax ID come back masked; the full values never leave the Lambda (Req 4.5).
 */
export const getOnboardingDraft = createAsyncThunk<OnboardDraft | null, string, { rejectValue: ErrorLike }>(
  'onboard/getOnboardingDraft',
  async (grant: string, { rejectWithValue }) => {
    try {
      const payload = await paidolService.getOnboardingDraft({ grant });
      if (!payload?.found) return null;
      return {
        invitationCode: payload.invitationCode,
        formData: payload.formData,
        rebatePercentage: payload.rebatePercentage,
      };
    } catch (error) {
      if (!wasAlreadyCaptured(error)) {
        // Never log the grant — it is a bearer credential for one draft.
        captureException(error);
      }
      return rejectWithValue(serializeError(toError(error)));
    }
  }
);

/**
 * Task 2.4. Previously this had neither `rejectValue` nor a catch, so a failure rejected with a raw
 * Error and never reached Sentry. The CDK operation returns a plain array of active products —
 * `isActive` is filtered server-side, so there is nothing to filter here.
 */
export const getHighnoteProducts = createAsyncThunk<OnboardingProduct[], void, { rejectValue: ErrorLike }>(
  'onboard/getHighnoteProducts',
  async (_, { rejectWithValue }) => {
    try {
      return (await paidolService.listProducts()) ?? [];
    } catch (error) {
      if (!wasAlreadyCaptured(error)) {
        captureException(error);
      }
      return rejectWithValue(serializeError(toError(error)));
    }
  }
);

/**
 * Task 2.3. Saves through the CDK, authorised by the session grant.
 *
 * Callers send **only the section they changed** (Req 3.3). The server merges at section level, so
 * omitted sections are preserved (Req 3.4) — landed in the companion repo as `1b719e3b`.
 *
 * The unit is the **section, not the field**: a section that is present replaces wholesale, and one
 * that is present must carry its sensitive field (`organizationProfile.identification`,
 * `primaryAuthorizedPerson.socialSecurityNumber`, `beneficialOwners[*].socialSecurityNumber`) or the
 * server answers 400. A masked value is accepted and resolves to the stored one. So send the whole
 * section, never a subset of its fields.
 *
 * Note an omitted key and an explicitly empty one differ: `{ administrator: {} }` clears that
 * section, while leaving the key out preserves it.
 *
 * Returns the payload it sent rather than a server echo — the response is status-only by design, so
 * a full SSN has one less way out.
 */
export const updateOnboardData = createAsyncThunk<
  OnboardFormDataDraft,
  OnboardFormDataDraft,
  { rejectValue: ErrorLike }
>('onboard/updateOnboardData', async (formData, { getState, rejectWithValue }) => {
  // Narrowed here rather than via the thunk's `state` generic, which would have to name the app's
  // full RootState and drags every other slice into this file's types.
  const grant = (getState() as { onboard: OnboardState }).onboard.session.grant;
  if (!grant) {
    return rejectWithValue({
      name: ONBOARD_SESSION_EXPIRED,
      message: translateOrKey('sessionExpiredVerifyAgain'),
    });
  }

  try {
    const input: SaveOnboardingDraftInput = { grant, formData: formData as OnboardingDraftFormDataInput };
    const result = await paidolService.saveOnboardingDraft(input);
    /**
     * An expired grant is a dead end unless it is named as one (raised in review on #2564).
     *
     * `ONBOARDING_GRANT_TTL_SECONDS` is an hour, and a five-step KYC form with several beneficial
     * owners plausibly outlasts it. The client is never told when the grant dies — nothing stores
     * `expiresInSeconds` — so the first sign is this call answering 403. That used to fall into the
     * branch below and put the server's literal `"Forbidden"` into the Alert: one word that means
     * nothing to an applicant, no way back, and every later Continue doing the same. Anything typed
     * after the hour mark was lost on closing the tab.
     *
     * 403 rather than the message text, because the status is structured and the text is not.
     * Clearing the grant in the reducer is what recovers: `OnBoard.tsx` then routes on
     * `session.status`, which is still `CODE_SENT`, straight to `/onboard/verify` with the
     * invitation code intact — and since the phone is verified by this point, Resend goes through
     * `startOnboardingSession`, which is the path that works for a verified number.
     */
    if (!result?.success && result?.statusCode === 403) {
      return rejectWithValue({
        name: ONBOARD_SESSION_EXPIRED,
        message: translateOrKey('sessionExpiredVerifyAgain'),
      });
    }
    if (!result?.success) {
      // The server names failing field paths and never values, so this is safe to surface.
      throw new Error(result?.message ?? translateOrKey('couldNotSaveApplication'));
    }
    return formData;
  } catch (error) {
    if (!wasAlreadyCaptured(error)) {
      captureException(error);
    }
    return rejectWithValue(serializeError(toError(error)));
  }
});

/**
 * What is safe to attach to a `registerOnboard` report.
 *
 * `args` used to be `{ hostname, onboardId }` and was logged whole. SEE-1274 swapped `onboardId` for
 * `grant`, which silently turned three existing diagnostics into a credential leak: the grant is a
 * bearer token authorising reads and writes of one applicant's full draft, so anyone with Sentry
 * access — or the browser console, on the applicant's own machine — could act as them until it
 * expired. `hostname` is the only field here that was ever diagnostic.
 */
const registerOnboardContext = (args: RegisterOnboardInput) => ({ hostname: args.hostname });

/** SEE-1388: a Highnote rejection also says which fields, so the form can send the applicant there. */
export type RegisterOnboardRejection = ErrorLike & { fieldErrors?: HighnoteFieldError[] };

export const registerOnboardFunction = createAsyncThunk<
  string | null | undefined,
  RegisterOnboardInput,
  { rejectValue: RegisterOnboardRejection }
>('onboard/registerOnboard', async (args, { rejectWithValue }) => {
  try {
    const result = await paidolService.registerOnboard(args);
    // Backend returns "success" or "error" as a string. Treat non-success as failure so Sentry reports it.
    if (result !== 'success') {
      const err: any = new Error('registerOnboard returned non-success result');
      // eslint-disable-next-line no-console
      console.error('registerOnboard failed', { args: registerOnboardContext(args), result });
      captureException(err, { extra: { args: registerOnboardContext(args), result } });
      throw Object.assign(err, { result, __sentryCaptured: true });
    }
    return result;
  } catch (error: any) {
    if (error instanceof HighnoteValidationError) {
      // Paths and codes only — the server never sends the rejected values.
      captureException(error, {
        extra: { args: registerOnboardContext(args), fieldErrors: error.fieldErrors },
      });
      return rejectWithValue({ ...serializeError(error), fieldErrors: error.fieldErrors });
    }
    if (!error?.__sentryCaptured) {
      captureException(error, {
        extra: { args: registerOnboardContext(args), result: error?.result },
      });
    }
    return rejectWithValue(serializeError(error));
  }
});

export interface OnboardChallengePayload {
  challenge: OnboardingSessionChallenge | null;
  /** Stamped here rather than in the reducer, which must stay pure. Epoch ms, or null. */
  resendAvailableAt: number | null;
}

/**
 * Driven by `resendAvailableInSeconds`, which is the server's own floor — never by
 * `expiresInSeconds`, which is the code's TTL. The two coincide only once a phone is bound; while it
 * is merely pending the code lives 300s but a resend is allowed after 60s, so counting down the TTL
 * would make a first-attempt typo cost five times what it should. The server value also folds in the
 * hourly cap, which the floor alone would under-report.
 *
 * Read on refusals too (`TOO_SOON`, `HOURLY_CAP_REACHED`), not just `CODE_SENT` — a refusal is
 * exactly when the applicant needs to know how long the wait is.
 */
/**
 * Diagnostic context for the three session thunks.
 *
 * The invitation code is deliberately NOT included, only whether one was present. It is one of the
 * two factors guarding an application: holding it is enough to bind a phone to any draft that has
 * not bound one yet, and from there to read everything the applicant has entered. That makes it a
 * credential rather than a correlation id, and Sentry access is far broader than draft access.
 *
 * The presence flag is what the value was actually being used for — separating "we sent an empty
 * code" from "the request failed in transport", which is a bug in our own form either way.
 */
const sessionErrorContext = (invitationCode: string | null | undefined) => ({
  hasInvitationCode: Boolean(invitationCode),
});

const resendTargetFrom = (challenge: OnboardingSessionChallenge | null): number | null =>
  challenge?.resendAvailableInSeconds == null ? null : Date.now() + challenge.resendAvailableInSeconds * 1000;

/**
 * Screen one: validates the invitation code and either asks for a phone (`NEEDS_PHONE`) or sends a
 * code to the already-bound one (`CODE_SENT`).
 *
 * Refusals (`TOO_SOON`, `HOURLY_CAP_REACHED`, `INVALID_INVITATION_CODE`, ...) come back as a
 * *fulfilled* `status`, not a thrown error — the screens branch on status. Only transport and
 * unexpected failures reject.
 */
export const startOnboardingSession = createAsyncThunk<
  OnboardChallengePayload,
  StartOnboardingSessionInput,
  { rejectValue: ErrorLike }
>('onboard/startOnboardingSession', async (args, { rejectWithValue }) => {
  try {
    const challenge = await paidolService.startOnboardingSession(args);
    return { challenge, resendAvailableAt: resendTargetFrom(challenge) };
  } catch (error) {
    if (!wasAlreadyCaptured(error)) {
      captureException(error, { extra: sessionErrorContext(args.invitationCode) });
    }
    return rejectWithValue(serializeError(toError(error)));
  }
});

/** Screen two: stores the number as PENDING and sends a code. `verifyOnboardingCode` commits it. */
export const bindOnboardingPhone = createAsyncThunk<
  OnboardChallengePayload,
  BindOnboardingPhoneInput,
  { rejectValue: ErrorLike }
>('onboard/bindOnboardingPhone', async (args, { rejectWithValue }) => {
  try {
    const challenge = await paidolService.bindOnboardingPhone(args);
    return { challenge, resendAvailableAt: resendTargetFrom(challenge) };
  } catch (error) {
    if (!wasAlreadyCaptured(error)) {
      // Never log `phoneNumber` — it is the thing being protected.
      captureException(error, { extra: sessionErrorContext(args.invitationCode) });
    }
    return rejectWithValue(serializeError(toError(error)));
  }
});

/** Proves control of the number and mints the session grant the rest of the flow carries. */
export const verifyOnboardingCode = createAsyncThunk<
  OnboardingSessionVerifyResult | null,
  VerifyOnboardingCodeInput,
  { rejectValue: ErrorLike }
>('onboard/verifyOnboardingCode', async (args, { rejectWithValue }) => {
  try {
    return await paidolService.verifyOnboardingCode(args);
  } catch (error) {
    if (!wasAlreadyCaptured(error)) {
      // `code` is a credential; keep it out of Sentry.
      captureException(error, { extra: sessionErrorContext(args.invitationCode) });
    }
    return rejectWithValue(serializeError(toError(error)));
  }
});

const onboardSlice = createSlice({
  name: 'onboard',
  initialState,
  reducers: {
    resetOnboardSlice: () => initialState,
    // Stores partial in-progress form values for the Back path. Mutations never read this —
    // it lives next to `onboard.formData`, not inside it, so a partial sub-object can't ride
    // along on the next `updateOnboardData` payload (gmunhoz0810's SEE-720 review).
    mergeOnboardFormDataDraft: (state, action: PayloadAction<OnboardFormDataDraft>) => {
      state.formDataDraft = { ...(state.formDataDraft ?? {}), ...action.payload };
    },
    clearOnboardFormDataDraftSlot: (state, action: PayloadAction<keyof OnboardFormDataDraft>) => {
      if (!state.formDataDraft) return;
      delete state.formDataDraft[action.payload];
      if (Object.keys(state.formDataDraft).length === 0) {
        state.formDataDraft = null;
      }
    },
  },
  extraReducers: (builder) => {
    /**
     * A new invitation code is a new applicant, so every piece of derived state has to go.
     *
     * `resetOnboardSlice` was dispatched only on logout, but `VerifyCode` has a Back link straight to
     * the invitation screen, so two applications in one tab is an ordinary path — it is what an
     * account manager walking a customer through the form, or QA, does all day. Without this,
     * `session.grant`, `onboard` and `formDataDraft` all survive into the second application, and
     * `formDataDraft` is the FIRST fallback in every step's `getInitialValues`. Applicant A's
     * date of birth, SSN and address pre-fill applicant B's form and are saved into B's draft on
     * Next. No URL manipulation required.
     *
     * Keyed on the invitation code changing, not on visiting the screen: `VerifyCode`'s resend calls
     * this same thunk with the SAME code, and wiping the session there would log the applicant out
     * mid-verification.
     *
     * Placed on `pending` rather than `fulfilled` so the stale data is gone while the request is in
     * flight, instead of briefly rendering behind it.
     */
    builder.addCase(startOnboardingSession.pending, (state, action) => {
      const submitted = action.meta.arg.invitationCode;
      if (state.session.invitationCode && state.session.invitationCode !== submitted) {
        state.onboard = null;
        state.formDataDraft = null;
        state.session = { ...initialSessionState };
      }
    });

    /**
     * Retain the number only when the server accepted it onto the draft as pending.
     *
     * `CODE_SCREEN_STATUSES` is exactly that set — it is the same list that decides whether this
     * bind sends the applicant to the code screen, so the number is kept precisely when a resend
     * could follow. `INVALID_PHONE` must not be retained (resending to a number the server
     * rejected would fail identically every time) and neither must `PHONE_ALREADY_BOUND`, which
     * means a *verified* number already exists and resend belongs on `startOnboardingSession`.
     */
    builder.addCase(bindOnboardingPhone.fulfilled, (state, action) => {
      const status = action.payload.challenge?.status;
      if (status && CODE_SCREEN_STATUSES.includes(status)) {
        state.session.pendingPhone = action.meta.arg.phoneNumber;
      }
    });

    builder.addCase(verifyOnboardingCode.fulfilled, (state, action) => {
      const result = action.payload;
      state.session.pending = false;
      state.session.attemptsRemaining = result?.attemptsRemaining ?? null;
      if (result?.verified && result.grant) {
        state.session.grant = result.grant;
        state.session.error = null;
        // Verified, so the server holds it as `boundPhone` now and any further resend goes through
        // `startOnboardingSession`. Nothing left to keep it for.
        state.session.pendingPhone = null;
      } else {
        state.session.grant = null;
      }
    });

    builder.addCase(getOnboardingDraft.fulfilled, (state, action) => {
      // `null` means the grant resolved no draft. Preserved as `null` rather than left undefined —
      // OnBoard.tsx distinguishes the two.
      state.onboard = action.payload;
    });

    // The save returns the formData it sent, not a server echo (the response is status-only by
    // design), so fold it back in to keep the store aligned with the row.
    //
    // Merged at section level, mirroring what the server now does. Req 3.3 means the payload is only
    // the section that changed, so replacing here would drop every other section from the store and
    // the next step would render blank fields over a row that is actually intact.
    builder.addCase(updateOnboardData.fulfilled, (state, action) => {
      state.onboard = {
        ...(state.onboard ?? {}),
        formData: { ...(state.onboard?.formData ?? {}), ...action.payload },
      };
    });
    /**
     * A failed save has to be visible somewhere, and this is the only place every caller shares.
     *
     * There was no rejected case at all: `session.error` was set only by the three session thunks, so
     * a 400 from `saveOnboardingDraft` left the store untouched and every step's Continue button
     * appeared to do nothing whatsoever. That silence is why the positional beneficial-owner mask bug
     * survived a live demo — the request failed, the applicant saw no error, no advance, and no
     * reason to report anything.
     *
     * The server's message is safe to surface verbatim: it names failing field paths and never
     * values, which is a property the backend maintains deliberately.
     */
    builder.addCase(updateOnboardData.rejected, (state, action) => {
      state.session.error = action.payload?.message ?? translateOrKey('couldNotSaveApplication');
      /**
       * Dropping the grant is the recovery, not just bookkeeping. `OnBoard.tsx` gates the whole form
       * on `session.grant`, so clearing it sends the applicant to `/onboard/verify` — `status` is
       * still `CODE_SENT` and `invitationCode` is intact — where the message set above explains why.
       * Leaving it in place is what made an expired session unrecoverable: every later Continue hit
       * the same 403 behind the same one-word banner.
       */
      if (action.payload?.name === ONBOARD_SESSION_EXPIRED) {
        state.session.grant = null;
      }
    });

    // Cleared when the next save starts, so a fixed error does not linger over a succeeding one.
    builder.addCase(updateOnboardData.pending, (state) => {
      state.session.error = null;
    });

    // RTK requires every addCase before any addMatcher, so every addCase stays above.
    builder.addCase(getHighnoteProducts.fulfilled, (state, action) => {
      if (action.payload) {
        state.highnoteProducts = action.payload;
      }
    });

    builder.addMatcher(
      isAnyOf(startOnboardingSession.fulfilled, bindOnboardingPhone.fulfilled),
      (state, action) => {
        const { challenge, resendAvailableAt } = action.payload;
        state.session.pending = false;
        state.session.status = challenge?.status ?? null;
        // The later screens post the code back with the phone and the OTP, so keep it here rather
        // than threading it through route state where a reload would lose it.
        state.session.invitationCode = action.meta.arg.invitationCode;
        // `maskedPhone` is absent on every path except CODE_SENT, including refusals. Keep the
        // previous value rather than blanking the number the code-entry screen is displaying.
        if (challenge?.maskedPhone) {
          state.session.maskedPhone = challenge.maskedPhone;
        }
        // Applied on refusals too: TOO_SOON and HOURLY_CAP_REACHED both carry the real remaining
        // wait, which is the number the applicant actually needs.
        state.session.resendAvailableAt = resendAvailableAt;
      }
    );

    builder.addMatcher(
      isAnyOf(startOnboardingSession.pending, bindOnboardingPhone.pending, verifyOnboardingCode.pending),
      (state) => {
        state.session.pending = true;
        state.session.error = null;
      }
    );

    builder.addMatcher(
      isAnyOf(startOnboardingSession.rejected, bindOnboardingPhone.rejected, verifyOnboardingCode.rejected),
      (state, action) => {
        state.session.pending = false;
        state.session.error = action.payload?.message ?? translateOrKey('somethingWentWrongTryAgain');
      }
    );
  },
});

export const { resetOnboardSlice, mergeOnboardFormDataDraft, clearOnboardFormDataDraftSlot } =
  onboardSlice.actions;

export const selectOnboard = (state: any): OnboardDraft | null | undefined => state.onboard.onboard;
export const selectOnboardFormDataDraft = (state: any): OnboardFormDataDraft | null =>
  state.onboard.formDataDraft;
export const selectHighnoteProducts = (state: any): OnboardingProduct[] | undefined =>
  state.onboard.highnoteProducts;
// Null-safe on `session` deliberately: preloaded stores (tests, and any caller building state by
// hand) predate this slice and omit it. Same failure shape as the highnoteProducts guard in 3.8.
export const selectOnboardSession = (state: any): OnboardSessionState =>
  state.onboard.session ?? initialSessionState;
export const selectOnboardSessionGrant = (state: any): string | null => state.onboard.session?.grant ?? null;

export default onboardSlice.reducer;
